import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, siteUrl } from '../supabaseClient';
import { getTopDriversWilson } from '../components/DELIVERY/services/reviewServices';
import { registerUser, signInWithIdentifier, validateEmail, validatePhone, validatePassword, detectIdentifierType } from '../services/authService';
import { useAuth } from '../components/USER/Auth/AuthContext';
import { ROLES } from '../constants';

import LandingHero from './landing/LandingHero';
import LandingAuthForm from './landing/LandingAuthForm';
import LandingTopDrivers from './landing/LandingTopDrivers';
import LandingFaq from './landing/LandingFaq';
import LandingFooter from './landing/LandingFooter';
import './LandingPage.css';

const FAQS = [
  { q: '¿Cómo pido un servicio?', a: 'Regístrate como cliente, ingresa tu ubicación y destino, y la aplicación te mostrará el precio estimado. Una vez confirmado, el conductor más cercano aceptará tu solicitud.' },
  { q: '¿Cómo funciona la asignación de viajes y conductores?', a: 'Al solicitar un servicio, el sistema localiza en tiempo real a los conductores activos y verificados más cercanos en un radio de hasta 10 km que coincidan con el vehículo solicitado (moto o carro). La asignación se realiza por proximidad GPS y de forma atómica: el conductor visualiza la ruta y tarifa estimada antes de aceptar, garantizando rapidez y evitando solicitudes duplicadas.' },
  { q: '¿Es seguro Bryemma?', a: 'Sí. Todos nuestros conductores pasan por un riguroso proceso de verificación digital y regulatoria conforme a las normativas del INTT (Cédula, Licencia, Certificación INTT, Carnet de Circulación, RIF y RCV). Además, contamos con botón de pánico, rastreo GPS en tiempo real dentro de la aplicación y soporte directo.' },
  { q: '¿Cómo se calculan los precios?', a: 'Los precios se basan en la distancia, el tiempo estimado y el tipo de servicio. Siempre verás el costo antes de confirmar.' },
];

function LandingPage() {
  const { currentUser, maxRoleLevel } = useAuth();
  const [mode, setMode] = useState('login');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [selectedRole, setSelectedRole] = useState(ROLES.PASSENGER);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [errors, setErrors] = useState({});
  const [topDrivers, setTopDrivers] = useState([]);
  const [installable, setInstallable] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    async function loadTopDrivers() {
      try {
        const drivers = await getTopDriversWilson(3);
        setTopDrivers(drivers);
      } catch (err) { console.error('Error loading top drivers:', err); }
    }
    loadTopDrivers();
  }, []);

  // PWA install prompt
  useEffect(() => {
    if (window.deferredPrompt) setInstallable(true);
    const handleInstallable = (e) => setInstallable(e.detail);
    window.addEventListener('pwa-installable', handleInstallable);
    return () => window.removeEventListener('pwa-installable', handleInstallable);
  }, []);

  const handleInstallPwa = async () => {
    if (!window.deferredPrompt) return;
    window.deferredPrompt.prompt();
    const { outcome } = await window.deferredPrompt.userChoice;
    if (outcome === 'accepted') window.deferredPrompt = null;
    setInstallable(false);
  };

  // Redirect admin/super_admin to /admin after OAuth
  useEffect(() => {
    if (currentUser && maxRoleLevel >= 4) {
      navigate('/admin', { replace: true });
    }
  }, [currentUser, maxRoleLevel, navigate]);

  // Mostrar mensaje de error si el callback de OAuth reportó un fallo
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hash = window.location.hash || '';
    const search = window.location.search || '';
    const params = new URLSearchParams(search);
    const errorDesc = params.get('error_description') || (hash.includes('error_description=') ? decodeURIComponent(hash.split('error_description=')[1].split('&')[0]) : null);
    if (errorDesc) {
      setFeedback({ type: 'error', message: 'Error con Google: ' + errorDesc.replace(/\+/g, ' ') });
    }
  }, []);

  const reset = useCallback((newMode) => {
    setMode(newMode);
    setIdentifier('');
    setPassword('');
    setConfirmPassword('');
    setName('');
    setFeedback(null);
    setErrors({});
    setShowPassword(false);
    setSelectedRole(ROLES.PASSENGER);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      localStorage.setItem('pending_oauth_role', selectedRole);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: siteUrl,
          queryParams: { access_type: 'offline', prompt: 'consent' },
        },
      });
      if (error) throw error;
    } catch (err) {
      setFeedback({ type: 'error', message: 'Error con Google: ' + err.message });
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const type = detectIdentifierType(identifier);

    if (!identifier) {
      newErrors.identifier = 'Ingresa tu correo o teléfono';
    } else if (type === 'unknown') {
      newErrors.identifier = 'Formato no válido';
    } else if (type === 'email' && !validateEmail(identifier)) {
      newErrors.identifier = 'Correo electrónico inválido';
    } else if (type === 'phone' && !validatePhone(identifier)) {
      newErrors.identifier = 'Número de teléfono inválido (ej: 04141234567 o +584141234567)';
    }

    if (!password) {
      newErrors.password = 'Ingresa tu contraseña';
    } else if (mode === 'register') {
      const passErrors = validatePassword(password);
      if (passErrors.length > 0) {
        newErrors.password = 'Debe tener: ' + passErrors.join(', ');
      }
    }

    if (mode === 'register') {
      if (!name.trim()) {
        newErrors.name = 'Ingresa tu nombre completo';
      }
      if (password !== confirmPassword) {
        newErrors.confirmPassword = 'Las contraseñas no coinciden';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setFeedback(null);

    try {
      if (mode === 'login') {
        const { data, error } = await signInWithIdentifier(identifier, password);
        if (error) throw error;
        if (data?.user) {
          navigate('/home');
        } else {
          throw new Error('Credenciales inválidas. Por favor verifica tu correo o contraseña.');
        }
      } else if (mode === 'register') {
        const type = detectIdentifierType(identifier);
        const res = await registerUser({
          email: type === 'email' ? identifier.trim() : undefined,
          phone: type === 'phone' ? identifier.trim() : undefined,
          password,
          name: name.trim(),
          role: selectedRole,
        });

        const requiresConfirmation = res?.user && !res?.session;
        let successMsg = 'Cuenta creada correctamente. Ya puedes iniciar sesión.';
        if (requiresConfirmation) {
          successMsg = 'Cuenta creada correctamente. Por favor confirma tu correo electrónico antes de iniciar sesión.';
        } else if (selectedRole === ROLES.DRIVER) {
          successMsg = '¡Cuenta de conductor creada! Inicia sesión para completar tu perfil de conductor y registrar tus vehículos.';
        }

        setFeedback({
          type: 'success',
          message: successMsg,
        });
        setIdentifier('');
        setPassword('');
        setConfirmPassword('');
        setName('');
        setMode('login');
      } else if (mode === 'recovery') {
        const type = detectIdentifierType(identifier);
        if (type === 'phone') {
          setFeedback({
            type: 'error',
            message: 'La recuperación de contraseña por teléfono no está disponible. Ingresa el correo asociado a tu cuenta o contacta a soporte.',
          });
          return;
        }
        const { error } = await supabase.auth.resetPasswordForEmail(identifier.trim());
        if (error) throw error;
        setFeedback({
          type: 'success',
          message: 'Revisa tu correo para restablecer tu contraseña.',
        });
      }
    } catch (err) {
      let msg = err.message || 'Ocurrió un error.';
      if (msg.includes('Invalid login credentials') || msg.includes('invalid_grant')) {
        msg = 'Correo, teléfono o contraseña incorrectos.';
      } else if (msg.includes('Email not confirmed')) {
        msg = 'Debes confirmar tu correo primero. Revisa tu bandeja de entrada.';
      } else if (msg.includes('Signups not allowed')) {
        msg = 'El registro no está habilitado en este momento.';
      } else if (msg.includes('Phone number not confirmed')) {
        msg = 'Debes confirmar tu número de teléfono.';
      }
      setFeedback({ type: 'error', message: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bry-landing-page min-vh-100 d-flex flex-column" style={{ backgroundColor: 'var(--bry-bg-primary, #F4F5F7)' }}>
      {/* Sección Hero */}
      <div className="py-4">
        <LandingHero
          onGetStarted={() => {
            const formElem = document.getElementById('landing-auth-form');
            if (formElem) formElem.scrollIntoView({ behavior: 'smooth' });
          }}
          onLearnMore={() => {
            const faqElem = document.getElementById('landing-faq-section');
            if (faqElem) faqElem.scrollIntoView({ behavior: 'smooth' });
          }}
          installable={installable}
          onInstallPwa={handleInstallPwa}
        />
      </div>

      {/* Formulario de Autenticación */}
      <div id="landing-auth-form" className="py-4">
        <LandingAuthForm
          mode={mode}
          setMode={setMode}
          identifier={identifier}
          setIdentifier={setIdentifier}
          name={name}
          setName={setName}
          password={password}
          setPassword={setPassword}
          confirmPassword={confirmPassword}
          setConfirmPassword={setConfirmPassword}
          selectedRole={selectedRole}
          setSelectedRole={setSelectedRole}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          loading={loading}
          feedback={feedback}
          errors={errors}
          handleSubmit={handleSubmit}
          handleGoogleLogin={handleGoogleLogin}
          reset={reset}
        />
      </div>

      {/* Conductores Destacados */}
      <LandingTopDrivers drivers={topDrivers} />

      {/* Preguntas Frecuentes */}
      <div id="landing-faq-section">
        <LandingFaq faqs={FAQS} />
      </div>

      {/* Pie de página */}
      <div className="mt-auto">
        <LandingFooter />
      </div>
    </div>
  );
}

export default LandingPage;
